<template>
  <a-card>
    <v-card class="technical-wrapper">
      <v-row align="center" justify="space-between" class="mx-0 px-2 grey darken-3 pdf-controls">
        <div class="d-flex align-center">
          <pcheck label="Edit Mode" v-model="editMode" class="white--text mr-4" />
          <pcheck
            v-if="editMode"
            label="Hide Reference"
            :value="hideReference"
            class="white--text mr-4"
            @input="updateHideReference"
          />
          <button
            v-if="editMode"
            type="button"
            class="program-extra-pages-jump"
            @click="scrollToExtraPages"
          >
            Extra pages
            <v-icon small class="program-extra-pages-jump__icon">south</v-icon>
          </button>
        </div>
        <div class="d-flex">
          <save-button :onsave="updateQuote" class="mr-3" />
          <g-button label="Print" icon="print" :disabled="editMode || assetsLoading" @onclick="printDocument" />
        </div>
      </v-row>
      <div class="technical-scroll grey darken-2">
        <div v-if="assetsLoading" class="program-assets-loading">
          <v-progress-linear indeterminate color="primary" class="mb-2" />
          <div class="caption white--text text-center">Loading program pages…</div>
        </div>
        <div id="printWrapper" class="pdf">
          <QuoteProgramWrapper
            v-if="quote"
            :quote="quote"
            :editMode="editMode"
            :routes="routes"
            :images="images"
            :program-pages="programPages"
            :supplier-product-data="supplierProductData"
            @update="updateProgram"
          />
        </div>
      </div>
    </v-card>
  </a-card>
</template>

<script>
import { PrintService } from "@/modules/media.print";
import { QuoteProgramWrapper } from "@/modules/quotes";
import QuotePrintCss from "./printStyle";
import { cloneDeep } from "lodash";
export default {
  name: "QuoteProgram",
  components: {
    QuoteProgramWrapper,
  },
  data() {
    return {
      editMode: false,
      quote: null,
      supplierProductData: {},
      images: [],
      routes: [],
      programPages: [],
      assetsLoading: false,
    };
  },
  computed: {
    hideReference() {
      return this.quote?.program?.hideReference === true;
    },
  },
  methods: {
    hydrateFromStore() {
      // Deep-clone only the editable quote. Sharing images/routes/pages avoids
      // duplicating multi-MB HTML (especially base64 images) in memory.
      const active = this.$store.getters["QuoteStore/active"];
      this.quote = active ? cloneDeep(active) : null;
      this.supplierProductData = this.$store.getters["QuoteStore/supplierProductData"] || {};
      this.images = this.$store.getters["QuoteStore/images"] || [];
      this.routes = this.$store.getters["QuoteStore/routes"] || [];
      this.programPages = this.$store.getters["QuoteStore/programPages"] || [];
      this.ensureProgramPages();
    },
    livePageIds() {
      const extraPages = this.quote?.program?.extraPages || [];
      return [
        ...new Set(
          extraPages
            .filter((entry) => entry && entry.mode !== "detached")
            .map((entry) => entry.pageId || entry.page_id)
            .filter(Boolean)
        ),
      ];
    },
    ensureProgramPages() {
      const ids = this.livePageIds();
      if (!ids.length) return;

      const have = new Set(
        (this.programPages || [])
          .filter((page) => page && page.id && page.content != null)
          .map((page) => page.id)
      );
      const missing = ids.filter((id) => !have.has(id));
      if (!missing.length) return;

      this.assetsLoading = true;
      this.$store
        .dispatch("ProgramPageStore/getProgramPagesByIds", missing)
        .then((pages) => {
          const byId = {};
          [...(this.programPages || []), ...(pages || [])].forEach((page) => {
            if (page?.id) byId[page.id] = page;
          });
          this.programPages = ids.map((id) => byId[id]).filter(Boolean).concat(
            Object.values(byId).filter((page) => !ids.includes(page.id))
          );
          this.$store.commit("QuoteStore/SET_PROGRAM_PAGES", this.programPages);
        })
        .catch((err) => this.$root.$error(err))
        .finally(() => {
          this.assetsLoading = false;
        });
    },
    updateQuote() {
      this.$store.commit("QuoteStore/SET_QUOTE", this.quote);
      const environment = this.quote.environment || this.$store.getters["AccountStore/environment"];

      return this.$store
        .dispatch("QuoteStore/updateQuote", environment)
        .then(() => {
          this.$store.commit("QuoteStore/SET_PROGRAM_PAGES", this.programPages);
          this.$store.commit("QuoteStore/SET_IMAGES", this.images);
          this.$store.commit("QuoteStore/SET_ROUTES", this.routes);
          this.$root.$success("Program updated successfully");
        })
        .catch((err) => this.$root.$error(err));
    },
    updateProgram(payload) {
      this.quote = payload.quote;
      if (payload.route) {
        this.routes.push(payload.route);
      }
      if (payload.image) {
        this.images.push(payload.image);
      }
      if (payload.programPage) {
        const exists = this.programPages.some((page) => page && page.id === payload.programPage.id);
        if (!exists) this.programPages.push(payload.programPage);
        this.$store.commit("QuoteStore/SET_PROGRAM_PAGES", this.programPages);
      }
    },
    updateHideReference(value) {
      this.quote = {
        ...this.quote,
        program: {
          ...(this.quote?.program || {}),
          hideReference: value,
        },
      };
    },
    scrollToExtraPages() {
      this.$nextTick(() => {
        const target = document.getElementById("program-extra-pages");
        if (!target) return;

        const preferred = this.$el && this.$el.querySelector(".technical-scroll");
        const scrollEl =
          (preferred && preferred.scrollHeight > preferred.clientHeight + 1 && preferred) ||
          this.getScrollParent(target);

        const headerOffset = 48;

        if (scrollEl) {
          const scrollRect = scrollEl.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          const nextTop = scrollEl.scrollTop + (targetRect.top - scrollRect.top) - headerOffset;
          if (typeof scrollEl.scrollTo === "function") {
            scrollEl.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
          } else {
            scrollEl.scrollTop = Math.max(0, nextTop);
          }
          return;
        }

        if (typeof target.scrollIntoView === "function") {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }

        const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      });
    },
    getScrollParent(el) {
      let node = el && el.parentElement;
      while (node && node !== document.body && node !== document.documentElement) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const canScroll = overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
        if (canScroll && node.scrollHeight > node.clientHeight + 1) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    },
    async printDocument() {
      const domElement = document.getElementById("printWrapper");
      if (!domElement) return;

      const clone = domElement.cloneNode(true);
      clone.querySelectorAll(".program-measure, .clear-btn, .v-btn, .program-extra-pages__controls, .no-print").forEach((el) => el.remove());

      const normalizeSrc = (src) => {
        if (!src) return "";
        if (src.startsWith("data:") || src.startsWith("blob:")) return src;
        try {
          return new URL(src, window.location.origin).href;
        } catch (_) {
          return src;
        }
      };

      const srcToOriginal = new Map();
      domElement.querySelectorAll("img").forEach((img) => {
        const src = normalizeSrc(img.getAttribute("src") || img.src);
        if (src && !srcToOriginal.has(src)) {
          srcToOriginal.set(src, img);
        }
      });

      const dataUrlCache = new Map();
      const cloneImages = [...clone.querySelectorAll("img")];
      await Promise.all(
        cloneImages.map(async (img) => {
          const src = normalizeSrc(img.getAttribute("src") || img.src);
          if (!src || src.startsWith("data:") || src.startsWith("blob:")) return;
          try {
            if (!dataUrlCache.has(src)) {
              const original = srcToOriginal.get(src);
              const fromCanvas = original ? this.imageElementToDataUrl(original) : null;
              dataUrlCache.set(src, fromCanvas ? Promise.resolve(fromCanvas) : this.toDataUrl(src));
            }
            img.src = await dataUrlCache.get(src);
          } catch (_) {
            // Keep original src if conversion fails.
          }
        })
      );

      PrintService.extractWordDocumentContent("QuoteProgram", clone.outerHTML, QuotePrintCss, {
        width: 750,
        postOpen: true,
        config: {
          landscape: false,
        },
        delayMs: 800,
      });
    },
    imageElementToDataUrl(img) {
      if (!img?.complete || !img.naturalWidth) return null;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        return canvas.toDataURL("image/png");
      } catch (_) {
        return null;
      }
    },
    toDataUrl(url) {
      const token = localStorage.getItem("token");
      const absoluteUrl = url.startsWith("http") || url.startsWith("data:") ? url : new URL(url, window.location.origin).href;
      return fetch(absoluteUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      })
        .then((response) => {
          if (!response.ok) throw new Error("Failed to load image");
          return response.blob();
        })
        .then(
          (blob) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            })
        );
    },
  },
  mounted() {
    this.hydrateFromStore();
    this.$root.$on("quote-refreshed", this.hydrateFromStore);
  },
  beforeDestroy() {
    this.$root.$off("quote-refreshed", this.hydrateFromStore);
  },
};
</script>

<style lang="scss">
.technical-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 150px);
  max-height: calc(100vh - 150px);

  .pdf-controls {
    position: absolute;
    top: 0;
    width: 100%;
    height: 35px;
    z-index: 100;
  }

  .program-extra-pages-jump {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.01em;
    cursor: pointer;
    line-height: 1;
  }

  .program-extra-pages-jump:hover,
  .program-extra-pages-jump:focus {
    color: #fff;
    text-decoration: underline;
    outline: none;
  }

  .program-extra-pages-jump__icon {
    color: inherit !important;
    font-size: 16px !important;
    margin-top: 1px;
  }

  .technical-scroll {
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .program-assets-loading {
    padding: 12px 24px 0;
  }

  .pdf {
    display: flex;
    flex-direction: column;
    width: 820px;
    background: transparent;
    margin: 24px auto;
    padding: 0;
    font-family: "Calibri", sans-serif;
    line-height: 1.2;

    .underline {
      text-decoration: underline;
    }

    .caps {
      text-transform: uppercase;
    }
  }
}
</style>
